import { useTranslation } from 'react-i18next';

import { Amount } from '@/components/amount';
import { cn } from '@/lib/utils';

import { usePartner } from './api';
import { balanceLines } from './partner-balance-lines';

/**
 * Wave 5D — the party's balance beside a document header: what WE OWE a
 * supplier (`owedByUs`) or what a customer OWES US (`owedToUs`), in the
 * partner's own currency, read off the partner record (POSTED documents
 * only, R9). Draws nothing until the partner has loaded, and never gates
 * anything — it is the figure a buyer wants beside a debit note, not a wall.
 * One figure per currency (owner decision, 30 Oct 2026), never a mixed sum.
 */
export function SupplierBalanceChip({
  partnerId,
  side,
  className,
}: {
  partnerId: string | null | undefined;
  side: 'supplier' | 'customer';
  className?: string | undefined;
}) {
  const { t } = useTranslation();
  const { data: partner } = usePartner(partnerId ?? undefined);
  if (!partner) return null;
  return (
    <span
      data-testid="partner-balance-chip"
      className={cn(
        'inline-flex flex-wrap items-center gap-1.5 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground',
        className,
      )}
    >
      <span>{side === 'supplier' ? t('partnerChip.weOwe') : t('partnerChip.owesUs')}</span>
      {balanceLines(partner, side).map((line) => (
        <Amount
          key={line.currency}
          value={line.value}
          currency={line.currency}
          className="text-foreground"
        />
      ))}
    </span>
  );
}
